import React, { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Copy, Check, ChevronRight, RefreshCw, RotateCcw, Pause, Loader2 } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { TextShimmer } from '@/components/ui/text-shimmer'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import type { Plugin } from 'unified'
import type { Root, Link } from 'mdast'
import { toast } from 'sonner'
import { FileEditWidget } from '@/components/features/file-edit-widget'
import { ChoiceBlock } from '@/components/features/chat-messages/choice-block'
import { FlashCardWidget } from '@/components/features/chat-messages/flash-card-widget'
import { MessageTimestamp, MessageReferences } from './message-common'
import {
  filterToolCallSyntax,
  filterProposedEditAcceptChoices,
  filterStaleLargeDeletionActionChoices,
  processContentWithDocumentChips as processContentWithDocumentChipsUtil,
  resolveCapBannerPhase,
  shouldShowAssistantStreamWorking,
  stripAgenticPauseProse,
} from '@/components/layout/chat-pane.utils'
import type { Message, ProposedEditData, DocumentReference } from '@/components/layout/chat-pane.types'
import { IngestionProgressMessage } from './ingestion-progress-message'
import { FileSummary } from './file-summary'
import { PrCard } from './pr-card'
import { isIngestionProgressMessage } from '@/lib/ingestion-message-parser'
import { isBasecampDraftFenceLanguage } from '@/lib/basecamp-draft-fence'
import {
  BasecampDraftAddToDocument,
  BasecampDraftFenceView,
} from '@/components/features/basecamp-draft-fence-view'
import { testIds } from '@/lib/test-ids'
import { chatMarkdownTableComponents } from '@/components/features/shared/chat-markdown-table'
import { cn } from '@/lib/utils'
import { extractFakeToolBrackets } from './fake-tool-brackets'
import { hideRawFlowNoteMarkers } from '@/lib/flow-note-markers'
import type { ChatMode } from '@/components/layout/chat-mode-selector'
import {
  hasIngestionFileBeenAutoOpened,
  markIngestionFileAutoOpened,
} from '@/lib/open-ingestion-conversation'
import {
  buildMessageDocumentsMap,
  chatMarkdownUrlTransform,
  isAllowedExternalChatLink,
  resolveChatMarkdownDocumentLink,
} from '@/lib/chat-markdown-links'
import { hrefForInAppNavigation } from '@/lib/maater-document-url'
import { InAppRouteLink } from '@/components/features/shared/in-app-route-link'
import { AssistantExternalLink } from './assistant-external-link'
import { MermaidDiagram } from '@/components/features/mermaid-diagram'
import { LiveWaitStatus } from '@/components/features/chat-messages/live-wait-status'
import { useLiveWaitElapsed } from '@/hooks/use-live-wait-elapsed'

function ChatDocumentLink({
  target,
  title,
  children,
  onDocumentClick,
}: {
  target: string
  title?: string
  children: React.ReactNode
  onDocumentClick: (idOrPath: string, event?: React.MouseEvent) => void | Promise<void>
}) {
  return (
    <button
      type="button"
      onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        e.stopPropagation()
        e.nativeEvent.stopImmediatePropagation()
        try {
          await onDocumentClick(target, e as React.MouseEvent)
        } catch (error) {
          console.error('[AssistantMessage] Error opening document:', error)
          toast.error('Failed to open document', {
            description: error instanceof Error ? error.message : 'Unknown error',
          })
        }
      }}
      className="text-primary hover:underline cursor-pointer font-medium bg-transparent border-0 p-0 m-0 text-left inline"
      title={title ? `Click to open: ${title} (CMD+click for new tab)` : undefined}
    >
      {children}
    </button>
  )
}

// Parse file summary from message content or metadata (pure function, defined outside component to avoid re-creation on render)
function parseFileSummary(content: string, metadata?: any) {
  // Try to get from metadata first (most reliable - has full path)
  if (metadata?.type === 'ingestion-success' && metadata.filePath) {
    return [{
      path: metadata.filePath,
      name: metadata.fileName || metadata.filePath.split('/').pop(),
      linesAdded: metadata.linesAdded || 0,
      linesRemoved: metadata.linesRemoved || 0
    }]
  }

  // Try to extract full path from "Created:" line (more reliable than "Files Changed")
  const createdMatch = content.match(/\*\*Created:\*\*\s*`([^`]+)`/)
  if (createdMatch) {
    const fullPath = createdMatch[1]
    const fileSummaryMatch = content.match(/\*\*Files Changed:\*\*\n- `[^`]+` \(\+(\d+)(?: -(\d+))?\)/)
    return [{
      path: fullPath,
      name: fullPath.split('/').pop(),
      linesAdded: fileSummaryMatch ? parseInt(fileSummaryMatch[1], 10) : 0,
      linesRemoved: fileSummaryMatch && fileSummaryMatch[2] ? parseInt(fileSummaryMatch[2], 10) : 0
    }]
  }

  // Parse from content (fallback - only filename, not full path)
  const fileSummaryMatch = content.match(/\*\*Files Changed:\*\*\n- `([^`]+)` \(\+(\d+)(?: -(\d+))?\)/)
  if (fileSummaryMatch) {
    const filename = fileSummaryMatch[1]
    return [{
      path: filename,
      name: filename.split('/').pop(),
      linesAdded: parseInt(fileSummaryMatch[2], 10),
      linesRemoved: fileSummaryMatch[3] ? parseInt(fileSummaryMatch[3], 10) : 0
    }]
  }

  return null
}

// Remark plugin to preserve doc: protocol in links
const remarkPreserveDocLinks: Plugin<[], Root> = () => {
  return (tree) => {
    const visit = (node: any) => {
      if (node.type === 'link' && node.url?.startsWith('doc:')) {
        // Preserve doc: protocol by storing it in a data attribute
        node.data = node.data || {}
        node.data.href = node.url // Store original href
        node.data.docId = node.url.replace('doc:', '') // Extract doc ID
      }
      if (node.children) {
        node.children.forEach(visit)
      }
    }
    visit(tree)
  }
}

// Filter tool call syntax from content — see filterToolCallSyntax in chat-pane.utils.ts

function FakeToolNarrationCollapse({
  labels,
  showRetry,
  onRetry,
}: {
  labels: string[]
  showRetry?: boolean
  onRetry?: () => void
}) {
  const [open, setOpen] = useState(false)
  const unique = useMemo(() => {
    const seen = new Set<string>()
    const out: string[] = []
    for (const label of labels) {
      const key = label.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      out.push(label)
    }
    return out
  }, [labels])

  const count = unique.length
  const summary =
    count === 0
      ? 'Described tool calls · none executed'
      : `Described ${count} tool call${count === 1 ? '' : 's'} · none executed`

  return (
    <div className="my-1.5" data-testid={testIds.chatPane.fakeToolCollapse}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        aria-expanded={open}
      >
        <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} />
        <span>{summary}</span>
      </button>
      {open && count > 0 && (
        <div className="mt-1.5 ml-5 max-h-40 space-y-0.5 overflow-y-auto text-xs text-muted-foreground/70 font-mono">
          {unique.slice(0, 20).map((label, i) => (
            <p key={`${label}-${i}`}>[{label}]</p>
          ))}
          {unique.length > 20 && <p>+{unique.length - 20} more</p>}
        </div>
      )}
      {showRetry && onRetry && (
        <div className="mt-1 ml-5 text-xs text-muted-foreground">
          <button
            type="button"
            className="hover:text-foreground hover:underline"
            onClick={onRetry}
          >
            Retry with explicit tool call
          </button>
        </div>
      )}
    </div>
  )
}

// Code block component with copy button and prototype-aligned styling
const CodeBlock = ({
  inline,
  children,
  copiedCode,
  onCopy,
  targetDocumentId,
  targetDocumentTitle,
  targetDocumentIdRef,
  targetDocumentTitleRef,
  className,
  node,
  ...props
}: {
  inline?: boolean
  children: React.ReactNode
  copiedCode: string | null
  onCopy: (code: string) => void
  targetDocumentId?: string | null
  targetDocumentTitle?: string | null
  targetDocumentIdRef?: React.RefObject<string | undefined>
  targetDocumentTitleRef?: React.RefObject<string | undefined>
  className?: string
  node?: any
  [key: string]: any
}) => {
  const codeString = String(children).replace(/\n$/, '')

  // Detect inline code: inline prop, no language class, or no newlines in content
  const hasNewlines = codeString.includes('\n')
  const hasLanguage = className?.startsWith('language-')
  const isInline = inline || (!hasLanguage && !hasNewlines)

  if (isInline) {
    return <code className="px-1.5 py-0.5 bg-muted rounded text-sm font-mono" {...props}>{children}</code>
  }

  // Extract language for display
  const language = className?.replace('language-', '') || 'code'

  const fenceLanguage = className || language
  if (isBasecampDraftFenceLanguage(fenceLanguage, codeString)) {
    return (
      <div className="my-2 w-full min-w-0 max-w-full">
        <BasecampDraftFenceView source={codeString} language={fenceLanguage} className="my-0" />
        <BasecampDraftAddToDocument
          fenceSource={codeString}
          documentId={targetDocumentId}
          documentTitle={targetDocumentTitle}
          documentIdRef={targetDocumentIdRef}
          documentTitleRef={targetDocumentTitleRef}
        />
      </div>
    )
  }

  // Live Mermaid diagrams in chat (same renderer as document preview)
  if (language === 'mermaid') {
    return (
      <div
        className="my-2 not-prose w-full max-w-full overflow-hidden chat-mermaid-block"
        data-testid={testIds.mermaid.chatBlock}
      >
        <MermaidDiagram source={codeString} showSourceToggle className="chat-mermaid-diagram" />
        <div className="flex justify-end px-2 pb-2">
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            onClick={() => onCopy(codeString)}
            title="Copy Mermaid source"
          >
            {copiedCode === codeString ? (
              <Check className="w-3.5 h-3.5 text-green-500" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
            Copy source
          </button>
        </div>
      </div>
    )
  }

  // Check if this is a markdown preview code block (ingestion success)
  const isMarkdownPreview = language === 'markdown' && codeString.includes('---\n')
  const fileName = isMarkdownPreview ? (codeString.match(/^---\n.*?\ntitle:\s*(.+?)\n/) || [])[1] : null

  return (
    <div className="relative group my-2 not-prose w-full max-w-full overflow-hidden">
      {/* Code generation view header (prototype-aligned) */}
      {isMarkdownPreview && fileName && (
        <div className="border border-border rounded-t-lg overflow-hidden bg-card">
          <div className="bg-muted/50 px-3 py-1.5 text-xs border-b border-border flex items-center justify-between">
            <span className="text-sm">{fileName}</span>
            <div className="flex items-center gap-2">
              <span className="text-green-500 text-xs">+{codeString.split('\n').length}</span>
            </div>
          </div>
        </div>
      )}
      <div
        className={cn(
          'chat-code-block bg-muted rounded-lg border border-border w-full max-w-full overflow-hidden',
          isMarkdownPreview && fileName ? 'rounded-t-none' : ''
        )}
      >
        <pre className="p-3 min-w-0 w-full max-w-full overflow-x-hidden overflow-y-auto whitespace-pre-wrap break-words [overflow-wrap:anywhere]" style={{ maxHeight: '300px' }}>
          <code className={`text-sm font-mono ${className || ''} block whitespace-pre-wrap break-words [overflow-wrap:anywhere]`} {...props}>{children}</code>
        </pre>
        <div className="absolute top-2 right-2 flex items-center gap-2">
          {!isMarkdownPreview && (
            <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{language}</span>
          )}
          <button
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-background/90 hover:bg-muted rounded border border-border"
            onClick={() => onCopy(codeString)}
            title="Copy code"
          >
            {copiedCode === codeString ? (
              <Check className="w-3.5 h-3.5 text-green-500" />
            ) : (
              <Copy className="w-3.5 h-3.5 text-muted-foreground" />
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

interface AssistantMessageProps {
  message: Message
  isAfterEdited: boolean
  copiedCode: string | null
  onCopyCode: (code: string) => void
  onDocumentClick?: (docId: string, event?: React.MouseEvent) => void | Promise<void>
  proposedEdit?: ProposedEditData | null
  onAcceptEdit?: (edit: ProposedEditData) => void
  onRejectEdit?: (edit: ProposedEditData) => void
  onUndoAcceptedEdit?: (edit: ProposedEditData) => void
  isApplyingEdit: boolean
  /** Current document ID (for proposed edits). Prefer activeDocumentIdRef when in memoized shell. */
  activeDocumentId?: string | null
  /** Open document title, used by the comment-draft "add to document" button. */
  activeDocumentTitle?: string | null
  /** Ref to read activeDocumentId at click time (avoids re-renders on tab switch) */
  activeDocumentIdRef?: React.RefObject<string | undefined>
  /** Ref to read the open document title at click time. */
  activeDocumentTitleRef?: React.RefObject<string | undefined>
  onEditBeforeApply?: () => void
  isLastMessage: boolean
  onRetryWithExplicitTool?: (prompt: string) => void
  onSetProposedEdit?: (edit: ProposedEditData) => void
  onSetDiffPreviewOpen?: (open: boolean) => void
  /** Called when the user clicks an action choice button */
  onActionChoice?: (choice: import('@/components/layout/chat-pane.types').ActionChoice) => void
  /** Plan-mode askQuestion Continue */
  onAskQuestionSubmit?: (
    ask: import('@/components/layout/chat-pane.types').AskQuestionData,
    selectedIds: string[],
    otherText?: string,
  ) => void
  onFlashCardPersist?: (patch: {
    progress: { index: number; total: number }
    savedAnswers: Record<string, { choice: string; correct: boolean; ms?: number }>
    done: boolean
  }) => void
  /** Hide trailing timestamp when the next message is a tool run (avoids orphaned time above the tool card). */
  hideTrailingTimestamp?: boolean
  /** Called when the user clicks checkpoint undo (after proposed-edit is resolved if any) */
  onUndoCheckpoints?: (checkpointIds: string[]) => Promise<void>
  /** After Stop: re-run from the last user message (streaming chat only). */
  onRepromptAfterStop?: () => void
  /** True while the latest assistant line is still streaming. */
  isStreaming?: boolean
  /**
   * True while any chat stream is active (uncapped assistant slot).
   * Used to collapse the agentic-cap banner after Continue when the follow-up stream ends.
   */
  isChatStreaming?: boolean
  /** True when a later assistant row exists (Continue already opened a follow-up slot). */
  hasLaterAssistant?: boolean
  /** Pane typing dots are already showing (do not double up Working... before first tokens). */
  isTyping?: boolean
  /** Re-run the previous user prompt via the same /edit path as save-without-changes (streaming chat). */
  onRegenerate?: () => void | Promise<void>
  /** Called to send "continue" when the agentic loop was capped. */
  onContinue?: () => void
  /**
   * When set, show Copy and write this string (full turn prose). When null/undefined and not last-in-turn,
   * hide the message-level Copy button (mid-turn segments).
   */
  turnCopyText?: string | null
  /** False for mid-turn assistant segments - hide Copy. Default true for backward compat. */
  showTurnCopy?: boolean
  /** Ask/Plan are read-only: do not nudge Retry with write tools. */
  chatMode?: ChatMode
  /** Hide the pending file row while the modified-files panel already lists it. */
  hidePendingFileWidget?: boolean
  /** Hide "Undo all changes this turn" while the files panel lists this turn. */
  hideTurnUndo?: boolean
}

export function AssistantMessage({
  message,
  isAfterEdited,
  copiedCode,
  onCopyCode,
  onDocumentClick,
  proposedEdit,
  onAcceptEdit,
  onRejectEdit,
  onUndoAcceptedEdit,
  isApplyingEdit,
  activeDocumentId,
  activeDocumentIdRef,
  activeDocumentTitle,
  activeDocumentTitleRef,
  onEditBeforeApply,
  isLastMessage,
  onRetryWithExplicitTool,
  onSetProposedEdit,
  onSetDiffPreviewOpen,
  onActionChoice,
  onAskQuestionSubmit,
  onFlashCardPersist,
  hideTrailingTimestamp,
  onUndoCheckpoints,
  onRepromptAfterStop,
  isStreaming = false,
  isChatStreaming = false,
  hasLaterAssistant = false,
  isTyping = false,
  onRegenerate,
  onContinue,
  turnCopyText = null,
  showTurnCopy = true,
  chatMode = 'agent',
  hidePendingFileWidget = false,
  hideTurnUndo = false,
}: AssistantMessageProps) {
  const processContentWithDocumentChips = (content: string, documentsMap?: Map<string, DocumentReference>): string => {
    return processContentWithDocumentChipsUtil(content, documentsMap)
  }

  const visibleActionChoices = useMemo(
    () =>
      filterProposedEditAcceptChoices(
        filterStaleLargeDeletionActionChoices(message.actionChoices),
      ),
    [message.actionChoices],
  )

  // Check for special message types
  const isIngestionProgress = isIngestionProgressMessage(message.content)
  const isPrCard = message.type === 'pr_card' && message.prCard

  // Memoized: only re-runs when message content/metadata changes, not on every parent re-render
  const fileSummary = useMemo(() => parseFileSummary(message.content, message.metadata), [message.content, message.metadata])
  const isIngestionSuccess = message.content.includes('✅ **Todo ingested successfully!**') || message.metadata?.type === 'ingestion-success'

  // Strip the **Files Changed:** markdown section when the FileSummary card will render it.
  // Backend `addSuccess()` writes that block into content for parseFileSummary to read; we hide
  // the redundant plain-text version from ReactMarkdown so the user sees only the styled card.
  const displayContent = useMemo(() => {
    let next = hideRawFlowNoteMarkers(stripAgenticPauseProse(message.content))
    // After Accept, hide the response-pipeline "pending your review / Accept the diff" note.
    if (message.proposedEdit) {
      next = next
        .split(/\n{2,}/)
        .filter((block) => !/edits proposed/i.test(block))
        .join('\n\n')
        .trim()
    }
    if (message.proposedEdit?.status === 'accepted' || message.proposedEdit?.status === 'rejected') {
      next = next
        .replace(
          /\n*Note:\s*The proposed edit is \*\*pending your review\*\*[^\n]*(?:\n|$)/gi,
          '\n',
        )
        .replace(/\n*Accept the diff to apply changes\.?\s*/gi, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
    }
    if (!fileSummary || fileSummary.length === 0) return next
    return next
      .replace(/\n*---\n+\*\*Files Changed:\*\*[\s\S]*$/, '')
      .trimEnd()
  }, [message.content, message.proposedEdit, fileSummary])

  const { labels: fakeToolLabels, cleaned: contentWithoutFakeTools } = useMemo(
    () => extractFakeToolBrackets(displayContent),
    [displayContent],
  )
  const hasFakeToolNarration = fakeToolLabels.length > 0
  const proseContent = hasFakeToolNarration ? contentWithoutFakeTools : displayContent
  const messageDocumentsMap = useMemo(
    () => buildMessageDocumentsMap(message),
    [message.content, message.documentsMap, message.toolResults],
  )
  const allowWriteOmissionNudge = chatMode !== 'plan' && chatMode !== 'ask'
  const showFakeToolRetry =
    allowWriteOmissionNudge &&
    hasFakeToolNarration &&
    isLastMessage &&
    !message.proposedEdit &&
    (!message.toolResults || message.toolResults.length === 0) &&
    Boolean(onRetryWithExplicitTool)


  const capBannerPhase = resolveCapBannerPhase({
    cappedAt: message.cappedAt,
    hasLaterAssistant,
    isChatStreaming,
  })
  const showStreamWorking = shouldShowAssistantStreamWorking({
    isChatStreaming,
    hasLaterAssistant,
    capBannerPhase,
    isTyping,
  })
  const liveWaitActive =
    isStreaming &&
    isLastMessage &&
    (Boolean(message.narrationPlaceholder) || showStreamWorking)
  const liveWaitElapsedMs = useLiveWaitElapsed(liveWaitActive)

  // Auto-open file on ingestion completion (only once per file, last message in view)
  React.useEffect(() => {
    if (!isLastMessage) return
    if (!isIngestionSuccess || !fileSummary || fileSummary.length === 0 || !onDocumentClick) {
      return
    }
    const filePath = fileSummary[0].path
    if (!filePath || hasIngestionFileBeenAutoOpened(filePath)) {
      return
    }

    const timer = window.setTimeout(async () => {
      console.log('[AssistantMessage] Auto-opening file after ingestion:', filePath)
      try {
        const { markProgrammaticNavigation } = await import('../../../hooks/use-url-sync')
        markProgrammaticNavigation()
        await onDocumentClick(filePath)
        markIngestionFileAutoOpened(filePath)
      } catch (error) {
        console.error('[AssistantMessage] Failed to auto-open document:', error)
      }
    }, 1500)
    return () => window.clearTimeout(timer)
  }, [isLastMessage, isIngestionSuccess, fileSummary, onDocumentClick])

  return (
    <div className="flex min-w-0 w-full max-w-full items-start group animate-fade-in">
      <div className={cn("min-w-0 flex-1 max-w-full", isIngestionProgress || isPrCard ? "space-y-0.5" : "space-y-1")}>
        {isPrCard ? (
          <PrCard card={message.prCard!} />
        ) : isIngestionProgress ? (
          <IngestionProgressMessage
            content={message.content}
            timestamp={message.timestamp}
            isLast={isLastMessage}
          />
        ) : (
          <>
          {message.narrationPlaceholder && displayContent.trim() ? (
            <div>
              <TextShimmer className="text-[15px] font-normal leading-relaxed">
                {displayContent.trim()}
              </TextShimmer>
              <LiveWaitStatus elapsedMs={liveWaitElapsedMs} />
            </div>
          ) : (
          <>
          {hasFakeToolNarration && (
            <FakeToolNarrationCollapse
              labels={fakeToolLabels}
              showRetry={showFakeToolRetry}
              onRetry={
                onRetryWithExplicitTool
                  ? () => onRetryWithExplicitTool('Call the tool directly - do not describe the action.')
                  : undefined
              }
            />
          )}
          {proseContent.trim() ? (
          <div
            className={cn(
              'prose dark:prose-invert min-w-0 max-w-full w-full overflow-x-hidden text-[15px] leading-[1.55]',
              'prose-p:my-1 prose-headings:mt-2.5 prose-headings:mb-1 prose-headings:leading-snug',
              'prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-li:leading-[1.5]',
              'prose-blockquote:my-1.5 prose-hr:my-2.5',
              'prose-pre:my-0 prose-pre:p-0 prose-pre:bg-transparent prose-pre:overflow-x-auto',
              isStreaming && 'chat-streaming-prose',
            )}
          >
            <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkPreserveDocLinks]}
            rehypePlugins={[rehypeHighlight]}
            urlTransform={chatMarkdownUrlTransform}
            components={{
              pre: ({ children }) => <>{children}</>,
              ...chatMarkdownTableComponents,
              // Use div for paragraphs to avoid nesting issues with code blocks
              p: ({ children, node, ...props }) => {
                void node
                return <div className="my-1" {...props}>{children}</div>
              },
              code: (props) => {
                const { inline, children, onCopy: _mdOnCopy, ...rest } = props as {
                  inline?: boolean
                  children?: React.ReactNode
                  className?: string
                  node?: unknown
                  [key: string]: unknown
                }
                return (
                <CodeBlock
                  inline={!!inline}
                  copiedCode={copiedCode}
                  onCopy={onCopyCode}
                  targetDocumentId={activeDocumentIdRef?.current || activeDocumentId}
                  targetDocumentTitle={activeDocumentTitleRef?.current || activeDocumentTitle}
                  targetDocumentIdRef={activeDocumentIdRef}
                  targetDocumentTitleRef={activeDocumentTitleRef}
                  {...rest}
                >
                  {children}
                </CodeBlock>
                )
              },
              // Render document links as clickable HTML anchors
              a: ({ href, children, node, ...props }) => {
                const { node: _, ...domProps } = props as any
                const linkText = String(children).trim()
                const docIdFromData = (node as any)?.data?.docId

                // External http(s) first - never route Basecamp/GitLab URLs through by-path.
                // Maater app hosts stay in-app (pathname + search via React Router).
                if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
                  const inAppHref = hrefForInAppNavigation(href)
                  if (inAppHref) {
                    return (
                      <InAppRouteLink
                        to={inAppHref}
                        className="text-primary hover:underline font-medium"
                        {...domProps}
                      >
                        {children}
                      </InAppRouteLink>
                    )
                  }

                  let urlHost: string | null = null
                  try {
                    urlHost = new URL(href).hostname
                  } catch {
                    // malformed URL - block it
                  }

                  if (isAllowedExternalChatLink(href)) {
                    return (
                      <AssistantExternalLink href={href} {...domProps}>
                        {children}
                      </AssistantExternalLink>
                    )
                  }

                  return (
                    <span
                      className="text-muted-foreground line-through decoration-dotted cursor-not-allowed"
                      title={`External link blocked: ${urlHost ?? href} is not in the allowed domains list`}
                    >
                      {children}
                    </span>
                  )
                }

                const resolvedTarget = resolveChatMarkdownDocumentLink(
                  href,
                  linkText,
                  messageDocumentsMap,
                  docIdFromData,
                )

                if (resolvedTarget && onDocumentClick) {
                  const docEntry = messageDocumentsMap.get(resolvedTarget)
                  return (
                    <ChatDocumentLink
                      target={resolvedTarget}
                      title={docEntry?.title}
                      onDocumentClick={onDocumentClick}
                    >
                      {children}
                    </ChatDocumentLink>
                  )
                }

                // Legacy partial UUID links: Document 8c2f4e5a...
                if (!href || href === '' || href === '#') {
                  const docMatch = linkText.match(/Document\s+([0-9a-f]{8,})/i)
                  if (docMatch && onDocumentClick) {
                    const partialId = docMatch[1].toLowerCase()
                    let fullId: string | null = null

                    for (const [id] of messageDocumentsMap.entries()) {
                      if (id.toLowerCase().startsWith(partialId)) {
                        fullId = id
                        break
                      }
                    }

                    if (fullId) {
                      const docEntry = messageDocumentsMap.get(fullId)
                      return (
                        <ChatDocumentLink
                          target={fullId}
                          title={docEntry?.title}
                          onDocumentClick={onDocumentClick}
                        >
                          {children}
                        </ChatDocumentLink>
                      )
                    }

                    return (
                      <button
                        type="button"
                        onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
                          e.preventDefault()
                          e.stopPropagation()
                          try {
                            const { searchDocuments } = await import('../../../lib/api')
                            const searchResults = await searchDocuments({
                              query: partialId.length >= 8 ? `id:${partialId}` : partialId,
                              limit: 20,
                            })
                            const matchingDoc = searchResults.results.find((doc) =>
                              doc.id.toLowerCase().startsWith(partialId),
                            )
                            if (matchingDoc) {
                              await onDocumentClick(matchingDoc.id, e as any)
                            } else {
                              toast.error('Document not found', {
                                description: `Could not find document with ID starting with "${partialId}".`,
                              })
                            }
                          } catch (error) {
                            console.error('[AssistantMessage] Error searching for document:', error)
                            toast.error('Failed to find document', {
                              description: error instanceof Error ? error.message : 'Unknown error',
                            })
                          }
                        }}
                        className="text-primary hover:underline cursor-pointer font-medium bg-transparent border-0 p-0 m-0 text-left inline"
                        title={`Click to search and open document: ${partialId}...`}
                      >
                        {children}
                      </button>
                    )
                  }
                }

                return <span className="text-muted-foreground">{children}</span>
              }
            }}
          >
              {processContentWithDocumentChips(filterToolCallSyntax(proseContent), messageDocumentsMap)}
            </ReactMarkdown>
          </div>
          ) : null}
          {/* File summary component */}
          {fileSummary && fileSummary.length > 0 && (
            <FileSummary
              files={fileSummary}
              onOpenFile={(filePath, event) => {
                void onDocumentClick?.(filePath, event)
              }}
            />
          )}
          </>
          )}
          </>
        )}

        {/* Checkpoint undo button — only shown after proposed-edit decision is made (not while pending) */}
        {message.checkpointIds && message.checkpointIds.length > 0 && onUndoCheckpoints &&
         !hideTurnUndo &&
         message.proposedEdit?.status !== 'pending' && (
          <div className="mt-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1.5 text-muted-foreground hover:text-foreground border-dashed"
              onClick={async () => {
                if (!window.confirm('Undo all document changes made in this turn?')) return
                await onUndoCheckpoints(message.checkpointIds!)
              }}
            >
              Undo all changes this turn
            </Button>
          </div>
        )}

        {/* Stopped indicator + re-prompt (Chat Reliability bet / Cursor parity) */}
        {message.aborted && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground/70 mt-1">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-muted-foreground/40" />
              {message.content ? 'Stopped' : 'Stopped before reply'}
            </span>
            {onRepromptAfterStop && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={onRepromptAfterStop}
              >
                <RefreshCw className="h-3 w-3" />
                Re-prompt
              </Button>
            )}
          </div>
        )}

        {/* Agentic cap - quiet inline Continue CTA */}
        {message.cappedAt && capBannerPhase !== 'hidden' && (
          <div
            className={cn(
              'flex items-center border-y border-border/60 py-2 transition-[padding,margin,gap] duration-200 ease-out',
              capBannerPhase === 'compact'
                ? 'mt-1.5 gap-2'
                : 'mt-2 flex-wrap gap-2',
            )}
            data-testid={testIds.chatPane.capBanner}
          >
            {capBannerPhase === 'compact' ? (
              <div
                className="flex min-w-0 flex-1 items-center gap-1.5 text-xs text-muted-foreground"
                data-testid={testIds.chatPane.capBannerContinuing}
              >
                <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
                <span>Continuing...</span>
              </div>
            ) : (
              <>
                <div className="flex min-w-0 flex-1 items-center gap-1.5 text-xs text-muted-foreground">
                  <Pause className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                  <span>
                    {message.cappedAt === 'max_tools'
                      ? 'More to do — tool limit reached'
                      : message.cappedAt === 'incomplete_write'
                        ? 'More to do - the reply was cut off'
                        : 'More to do — round limit reached'}
                  </span>
                </div>
                {onContinue && (
                  <button
                    type="button"
                    className="text-xs font-medium text-foreground/80 hover:text-foreground hover:underline shrink-0"
                    data-testid={testIds.chatPane.continueWorking}
                    onClick={() => onContinue()}
                  >
                    Continue working
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {showStreamWorking && (
          <div
            className="mt-1.5 text-xs text-muted-foreground"
            data-testid={testIds.chatPane.streamWorking}
            aria-live="polite"
          >
            <div className="flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
              <span>Working...</span>
            </div>
            <LiveWaitStatus elapsedMs={liveWaitElapsedMs} />
          </div>
        )}

        {/* Hover actions - turn-level Copy (hide mid-turn and ingestion progress) */}
        {!isIngestionProgress && showTurnCopy && (
          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            {isLastMessage && !isStreaming && !message.proposedEdit && onRegenerate && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-muted-foreground hover:text-foreground"
                type="button"
                title="Regenerate reply"
                aria-label="Regenerate reply"
                onClick={() => void onRegenerate()}
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Regenerate
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground hover:text-foreground"
              title="Copy entire turn"
              aria-label="Copy entire turn"
              onClick={async () => {
                try {
                  const text =
                    (turnCopyText && turnCopyText.trim()) ||
                    filterToolCallSyntax(message.content)
                  await navigator.clipboard.writeText(text)
                  toast.success('Copied to clipboard')
                } catch (error) {
                  console.error('Failed to copy:', error)
                  toast.error('Failed to copy')
                }
              }}
            >
              <Copy className="h-3 w-3 mr-1" />
              Copy
            </Button>
          </div>
        )}

        {/* Proposed Edit - compact row only (no duplicate DocumentResultCard).
            Skip empty hydrate stubs (no original/proposed) so Review never opens a +0/-0 modal. */}
        {message.proposedEdit &&
          !hidePendingFileWidget &&
          (Boolean(message.proposedEdit.original) || Boolean(message.proposedEdit.proposed)) &&
          message.proposedEdit.original !== message.proposedEdit.proposed && (
          <div className="mt-2">
            <FileEditWidget
              fileName={message.proposedEdit.filename}
              displayPath={message.proposedEdit.displayPath}
              url={message.proposedEdit.url}
              title={message.proposedEdit.title}
              additions={message.proposedEdit.additions}
              deletions={message.proposedEdit.deletions}
              status={message.proposedEdit.status}
              changeStatus={message.proposedEdit.changeStatus}
              requiresAcceptance={message.proposedEdit.requiresAcceptance ?? true}
              isProcessing={isApplyingEdit}
              onOpen={
                onDocumentClick && message.proposedEdit.documentId
                  ? async () => {
                      if (message.proposedEdit?.documentId) {
                        window.dispatchEvent(new CustomEvent('document-updated', {
                          detail: { documentId: message.proposedEdit.documentId }
                        }))
                      }
                      await onDocumentClick(message.proposedEdit!.documentId!)
                    }
                  : undefined
              }
              onReview={() => {
                if (onSetProposedEdit && message.proposedEdit) {
                  onSetProposedEdit(message.proposedEdit)
                }
                if (onSetDiffPreviewOpen) {
                  onSetDiffPreviewOpen(true)
                }
              }}
              onAccept={
                message.proposedEdit.requiresAcceptance === false
                  ? undefined
                  : () => onAcceptEdit?.(message.proposedEdit!)
              }
              onReject={
                message.proposedEdit.requiresAcceptance === false
                  ? undefined
                  : () => onRejectEdit?.(message.proposedEdit!)
              }
              onUndoAccepted={
                message.proposedEdit.status === 'accepted' && onUndoAcceptedEdit
                  ? () => onUndoAcceptedEdit!(message.proposedEdit!)
                  : undefined
              }
            />
          </div>
        )}

        {/* AI Didn't Execute Warning - detect if AI talked about editing but didn't call tools */}
        {allowWriteOmissionNudge &&
        message.role === 'assistant' &&
         isLastMessage &&
         !hasFakeToolNarration &&
         !message.proposedEdit &&
         (!message.toolResults || message.toolResults.length === 0) &&
         (() => {
           const c = message.content.toLowerCase()
           return (
             c.includes("i'll add") ||
             c.includes('i will add') ||
             c.includes('let me proceed') ||
             c.includes('let me add') ||
             c.includes("i'll update") ||
             c.includes("i'll edit") ||
             c.includes("i'll create") ||
             c.includes("i've created") ||
             c.includes('i have created') ||
             c.includes('folder path created') ||
             c.includes('document created') ||
             (c.includes('perfect') && c.includes('created'))
           )
         })() && (
          <div className="mt-2 py-2 border-y border-border/60">
            <p className="text-xs text-muted-foreground leading-relaxed">
              The assistant described what it would do but did not call the tool.
              {onRetryWithExplicitTool && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="hover:text-foreground hover:underline"
                    onClick={() => {
                      const c = message.content.toLowerCase()
                      const wantsCreate =
                        c.includes('folder') ||
                        c.includes('create') ||
                        c.includes('document') ||
                        c.includes('file')
                      const docId = activeDocumentIdRef?.current ?? activeDocumentId
                      const explicitPrompt = wantsCreate
                        ? 'Use createDocument() with folderPath under my active workspace scope. Create the starter document now - do not describe the action, call the tool.'
                        : docId
                          ? `Use appendToDocument() tool on document ${docId} to add content to the bottom`
                          : 'Use the appendToDocument() tool to add content'
                      onRetryWithExplicitTool(explicitPrompt)
                    }}
                  >
                    Retry with explicit tool call
                  </button>
                </>
              )}
            </p>
          </div>
        )}

        {/* Action Choices - primary pill + muted secondary links (Cursor-style).
            Hide legacy Force apply / Use patch / Cancel (Review modal Accept is enough). */}
        {visibleActionChoices.length > 0 && onActionChoice && (
          <div className="mt-2 flex flex-col gap-2">
            {visibleActionChoices
              .filter((c) => c.variant === 'primary')
              .map((choice, idx) => (
                <Button
                  key={`primary-${choice.id || idx}`}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-fit max-w-full text-xs font-medium"
                  data-testid={testIds.chatPane.actionChoicePrimary(idx)}
                  onClick={() => onActionChoice(choice)}
                >
                  {choice.label}
                </Button>
              ))}
            {visibleActionChoices.some((c) => c.variant !== 'primary') ? (
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {visibleActionChoices
                  .filter((c) => c.variant !== 'primary')
                  .map((choice, idx) => (
                    <button
                      key={`secondary-${choice.id || idx}`}
                      type="button"
                      className={cn(
                        'text-xs hover:underline',
                        choice.variant === 'destructive'
                          ? 'text-red-500/80 hover:text-red-500'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                      data-testid={testIds.chatPane.actionChoiceSecondary(idx)}
                      onClick={() => onActionChoice(choice)}
                    >
                      {choice.label}
                    </button>
                  ))}
              </div>
            ) : null}
          </div>
        )}

        {message.askQuestion &&
        (message.askQuestion.answered || onAskQuestionSubmit) ? (
          <ChoiceBlock
            policy="plan-ask"
            question={message.askQuestion.question}
            options={message.askQuestion.options}
            selectionMode={message.askQuestion.allowMultiple ? 'multiple' : 'single'}
            allowOther={message.askQuestion.allowOther !== false}
            answered={message.askQuestion.answered ?? null}
            onSubmit={
              message.askQuestion.answered
                ? undefined
                : ({ selectedIds, otherText }) =>
                    onAskQuestionSubmit?.(
                      message.askQuestion!,
                      selectedIds,
                      otherText,
                    )
            }
          />
        ) : null}

        {message.flashCardSession ? (
          <FlashCardWidget
            session={message.flashCardSession}
            onPersist={onFlashCardPersist}
          />
        ) : null}

        {/* Timestamp and References (hide timestamp for ingestion progress) */}
        {!isIngestionProgress && !hideTrailingTimestamp && (
          <MessageTimestamp timestamp={message.timestamp} />
        )}
        <MessageReferences references={message.references} />
      </div>
    </div>
  )
}

